import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './state';
import * as productApi from 'src/api/product';
import { AppState } from 'src/store';
import { TaxStatus } from 'src/admin-entity/enum/product/tax-status.enum';
import { ProductImage } from 'src/admin-entity/interface/product/product-image.interface';
import { ProductLabelShippingMethodEnum } from 'src/admin-entity/enum/product/product-label-shipping-method.enum';
import { ProductLabelRecommendEnum } from 'src/admin-entity/enum/product/product-label-recommend.enum';
import { ProductOptionCombination } from 'src/admin-entity/interface/product/product-option-combination.interface';
import { ProductOptionGroup } from 'src/admin-entity/interface/product/product-option-group.interface';
import { ProductOrderCondition } from 'src/admin-entity/enum/product/product-order-condition.enum';
import { ProductOptionStatus } from 'src/admin-entity/enum/product/product-option-status.enum';
import { Category } from 'src/admin-entity/interface/category/category.interface';
import { ProductStatus } from 'src/admin-entity/enum/product/product-status.enum';
import { ProductSaleType } from 'src/admin-entity/interface/product/product-sale-type.interface';
import { ProductOld } from 'src/admin-entity/interface/product/product-old.interface';
import { dateFormatter } from 'src/utils/dateFormatter';
import { ProductRelatedType } from 'src/admin-entity/enum/product/product-related-type.enum';
import { ProductRelatedProduct } from 'src/admin-entity/interface/product/product-related-product.interface';
import { HomeDisplayWithProduct } from 'src/admin-entity/interface/product/product-home-display-with-product.interface';
import { ProductImageRepresent } from 'src/admin-entity/interface/product/product-image-represent.interface';
import { BASE_URL } from 'src/config/api';
import client from 'src/api/client';
import { setProgress } from '../progress/progressSlice';
import { AxiosResponse } from 'axios';
import { Product } from 'src/admin-entity/interface/product/product.interface';

const getProduct = async (id: number): Promise<Product> => {
    const product = (await productApi.getProduct(id)).data;
    const productInfo = (await productApi.getProductDetailInfo(id)).data;
    return {
        ...product,
        ...productInfo,
        productSaleType: product.productSaleType,
        productDetail: {
            ...product.productDetail,
            ...productInfo.productDetail,
        },
    };
};

const getLiveProductOption = async (id: number): Promise<any> => {
    const productOptionInfo = (await productApi.getProductDetailInfo(id)).data;
    return productOptionInfo;
};

export const fetchLiveProductOptionData = createAsyncThunk(
    'product/getLiveProductOption',
    async (id: number, { rejectWithValue }) => {
        try {
            return await getLiveProductOption(id);
        } catch (err) {
            return rejectWithValue(err?.response?.data);
        }
    },
);

export const getProductDetail = createAsyncThunk(
    `product/getProductDetail`,
    async (id: number, { dispatch, rejectWithValue }) => {
        try {
            dispatch(initProductData());
            const res = await productApi.getProductDetail(id);
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const createSimpleProduct = createAsyncThunk(`product/createSimpleProduct`, async (_, { rejectWithValue }) => {
    try {
        const res = await productApi.createSimpleProduct();
        return res.data;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const addProduct = createAsyncThunk(`product/addProduct`, async (_, { getState, rejectWithValue }) => {
    try {
        const res = await productApi.addProduct((getState() as AppState).product.product);
        return res.data;
    } catch (err) {
        return rejectWithValue(err);
    }
});

export const updateProductBasicInfo = createAsyncThunk(
    `product/updateProductBasicInfo`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const { product } = (getState() as AppState).product;
            const res = await productApi.updateProductBasicInfo(`${product.id}`, {
                name: product.name,
                contents: product.contents,
                listPrice: product.productPrice.listPrice,
                price: product.productPrice.price,
                specialSalePrice: product.productPrice.specialSalePrice,
                productDetail: {
                    taxStatus: product.productDetail.taxStatus,
                    adminMemo: product.productDetail.adminMemo,
                },
                productExtra: product.productExtra,
                productType: product.productType,
                productFunding: {
                    targetQuantity: product.productFunding.targetQuantity,
                },
                summaryTitle: product.summaryTitle,
                isInternationalShipping: product.isInternationalShipping,
                tossVariantKey: product.tossVariantKey,
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const updateProductDistributor = createAsyncThunk(
    `product/updateProductDistributor`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const { product } = (getState() as AppState).product;
            await productApi.updateProductDistributor(`${product.id}`, {
                distributor: {
                    id: product.distributor.id,
                },
            });
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addProductImageRepresent = createAsyncThunk(
    `product/addProductImageRepresent`,
    async (imgFiles: FileList, { getState, dispatch, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;

            const url = `${BASE_URL}/v2/admin/product/${productId}/image/represent`;
            const form = new FormData();
            for (let i = 0; i < imgFiles.length; i++) {
                form.append(`imgFiles`, imgFiles.item(i));
            }
            const res: AxiosResponse<ProductImageRepresent[]> = await client.post(url, form, {
                onUploadProgress: (e: ProgressEvent) => {
                    dispatch(setProgress(Math.floor((e.loaded / e.total) * 100)));
                },
            });

            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);
export const fetchProductData = createAsyncThunk('product/getProduct', async (id: number, { rejectWithValue }) => {
    try {
        return await getProduct(id);
    } catch (err) {
        return rejectWithValue(err?.response?.data);
    }
});

export const sortingProductImageRepresent = createAsyncThunk(
    `product/sortingProductImageRepresent`,
    async (changeData: ProductImageRepresent[], { getState, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;
            const res = await productApi.sortingProductImageRepresent(
                productId,
                changeData.map((item, index) => {
                    return {
                        id: item.id,
                        sort: index,
                    };
                }),
            );
            return changeData;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const deleteProductImageRepresent = createAsyncThunk(
    `product/deleteProductImageRepresent`,
    async (ids: number[], { getState, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;
            await productApi.deleteProductImageRepresent(productId, ids);
            return ids;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const addProductImages = createAsyncThunk(
    `product/addProductImages`,
    async (imgFiles: FileList, { getState, dispatch, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;
            const url = `${BASE_URL}/v2/admin/product/${productId}/image`;
            const form = new FormData();
            for (let i = 0; i < imgFiles.length; i++) {
                form.append(`imgFiles`, imgFiles.item(i));
            }

            const res: AxiosResponse<ProductImage[]> = await client.post(url, form, {
                onUploadProgress: (e: ProgressEvent) => {
                    dispatch(setProgress(Math.floor((e.loaded / e.total) * 100)));
                },
            });

            return res.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const sortingProductImages = createAsyncThunk(
    `product/sortingProductImages`,
    async (changeData: ProductImage[], { getState, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;
            const res = await productApi.updateProductImageSort(
                productId,
                changeData.map((item, index) => {
                    return {
                        id: item.id,
                        sort: index,
                    };
                }),
            );
            return changeData;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const deleteProductImages = createAsyncThunk(
    `product/deleteProductImages`,
    async (ids: number[], { getState, rejectWithValue }) => {
        try {
            const productId = (getState() as AppState).product.product.id;
            await productApi.deleteProductImage(productId, ids);
            return ids;
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

export const productSlice = createSlice({
    name: 'productSlice',
    initialState,
    reducers: {
        initProductData: () => {
            return { ...initialState };
        },
        setProduct: (state, action: PayloadAction<ProductOld>) => {
            state.product = action.payload;
        },
        // 🟢 [추가]: 결제 수단 상태를 변경하는 reducer
        updateTossVariantKey: (state, action: PayloadAction<string>) => {
            state.product.tossVariantKey = action.payload;
        },
        updateProductName: (state, action: PayloadAction<string>) => {
            state.product.name = action.payload;
        },
        updateIsInternationalShipping: (state, action: PayloadAction<boolean>) => {
            state.product.isInternationalShipping = action.payload;
        },
        updateProductType: (state, action: PayloadAction<1 | 2 | 4>) => {
            state.product.productType = action.payload;
            if (action.payload === 4) {
                state.product.bundleShippingFeeType = 1;
            } else if (action.payload === 1) {
                state.product.bundleShippingFeeType = 0;
            }
        },
        updateProductTargetQuantity: (state, action: PayloadAction<number>) => {
            state.product.productFunding.targetQuantity = action.payload;
        },
        updatePrice: (state, action: PayloadAction<string>) => {
            state.product.productPrice.price = +action.payload;
        },
        updateListPrice: (state, action: PayloadAction<string>) => {
            state.product.productPrice.listPrice = +action.payload;
        },
        updateSpecialPrice: (state, action: PayloadAction<string>) => {
            state.product.productPrice.specialSalePrice = +action.payload;
        },
        updateTaxStatus: (state, action: PayloadAction<TaxStatus>) => {
            state.product.productDetail.taxStatus = action.payload;
        },
        updateAdminMemo: (state, action: PayloadAction<string>) => {
            state.product.productDetail.adminMemo = action.payload;
        },
        updateContents: (state, action: PayloadAction<string>) => {
            state.product.contents = action.payload;
        },
        updateUseKakaosync: (state, action: PayloadAction<boolean>) => {
            state.product.productExtra.useKakaosync = action.payload;
        },
        updateRepresentImageFiles: (state, action: PayloadAction<ProductImageRepresent[]>) => {
            state.product.productImageRepresent = [...action.payload];
        },
        updateProductImages: (state, action: PayloadAction<ProductImage[]>) => {
            state.product.productDetail.productImages = [...action.payload];
        },
        updateDeeplink: (state, action: PayloadAction<string>) => {
            state.product.deeplink = action.payload;
        },
        updateVideoUrl: (state, action: PayloadAction<string>) => {
            state.product.productDetail.videoUrl = action.payload;
        },
        updateImageOriginPath: (state, action: PayloadAction<string>) => {
            state.product.productExtra.imageOriginPath = action.payload;
        },
        updateLabelShippingMethod: (state, action: PayloadAction<ProductLabelShippingMethodEnum>) => {
            state.product.labelShippingMethod = action.payload;
        },
        updateLabelRecommend: (state, action: PayloadAction<ProductLabelRecommendEnum>) => {
            state.product.labelRecommend = action.payload;
        },
        updateProductOptionCombinations: (state, action: PayloadAction<ProductOptionCombination[]>) => {
            state.product.productDetail.productOptionCombinations = action.payload;
        },
        initSelectedComb: state => {
            state.selectedComb = [];
        },
        updateProductOptionCombinationStatus: (
            state,
            action: PayloadAction<{ index: number; status: ProductOptionStatus }>,
        ) => {
            state.product.productDetail.productOptionCombinations[action.payload.index].productOptionStatus =
                action.payload.status;
        },
        updateProductOptionCombinationOptionStock: (state, action: PayloadAction<{ index: number; value: number }>) => {
            state.product.productDetail.productOptionCombinations[action.payload.index].optionStock =
                action.payload.value;
        },
        updateProductOptionCombinationPriceAndSupplyPrice: (
            state,
            action: PayloadAction<{
                index: number;
                key: 'price' | 'supplyPrice' | 'specialSalePrice';
                value: number;
            }>,
        ) => {
            state.product.productDetail.productOptionCombinations[action.payload.index].productOptionPrice[
                action.payload.key
            ] = action.payload.value;
        },
        updateProductOptionGroupName: (state, action: PayloadAction<{ index: number; name: string }>) => {
            state.product.productDetail.productOptionGroups[action.payload.index].name = action.payload.name;
        },
        updateProductOptionGroups: (state, action: PayloadAction<ProductOptionGroup[]>) => {
            state.product.productDetail.productOptionGroups = action.payload;
        },
        addProductOption: (state, action: PayloadAction<number>) => {
            state.product.productDetail.productOptionGroups[action.payload].productOptions.push({ name: '' });
        },
        removeProductOption: (state, action: PayloadAction<{ optionGroupIndex: number; optionIndex: number }>) => {
            state.product.productDetail.productOptionGroups[action.payload.optionGroupIndex].productOptions.splice(
                action.payload.optionIndex,
                1,
            );
        },
        updateProductOptionName: (
            state,
            action: PayloadAction<{ optionGroupIndex: number; optionIndex: number; value: string }>,
        ) => {
            state.product.productDetail.productOptionGroups[action.payload.optionGroupIndex].productOptions[
                action.payload.optionIndex
            ].name = action.payload.value;
        },
        updateProductOrderConditions: (state, action: PayloadAction<ProductOrderCondition[]>) => {
            state.product.productOrderConditions = action.payload;
        },
        updateRestrictedStockOrder: (state, action: PayloadAction<number>) => {
            state.product.restrictedStockOrder = action.payload;
        },
        updateCategory: (state, action: PayloadAction<Category>) => {
            state.product.category = action.payload;
        },
        updateChildCategory: (state, action: PayloadAction<Category>) => {
            state.product.category = {
                id: action.payload.id,
                name: action.payload.name,
                parent: state.product.category,
            };
        },
        updateProductStatus: (state, action: PayloadAction<ProductStatus>) => {
            state.product.productStatus = action.payload;
        },
        updateProductSaleType: (state, action: PayloadAction<ProductSaleType['saleType']>) => {
            state.product.productSaleType.saleType = action.payload;
        },
        updateLimitedPeriodSaleEndDate: (state, action: PayloadAction<string>) => {
            state.product.productSaleType.limitedPeriodSaleEndDate = action.payload;
        },
        updateIsCommingSoon: (state, action: PayloadAction<boolean>) => {
            state.product.productSaleType.isCommingSoon = action.payload;
        },
        updateCommingSoonDate: (state, action: PayloadAction<string>) => {
            state.product.productSaleType.commingSoonDate = action.payload;
        },
        updateNoResale: (state, action: PayloadAction<boolean>) => {
            state.product.productSaleType.noResale = action.payload;
        },
        updateIsVisibleInList: (state, action: PayloadAction<boolean>) => {
            state.product.isVisibleInList = action.payload;
        },
        updateUseGift: (state, action: PayloadAction<boolean>) => {
            state.product.useGift = action.payload;
        },
        updateGiftExpireType: (state, action: PayloadAction<ProductOld['giftExpireType']>) => {
            state.product.giftExpireType = action.payload;
        },
        updateGiftExpireDay: (state, action: PayloadAction<number>) => {
            state.product.giftExpireDays = action.payload;
        },
        updateGiftFixedExpireDate: (state, action: PayloadAction<string>) => {
            state.product.giftFixedExpireDate = action.payload;
        },
        updateUnstoringDaysAfter: (state, action: PayloadAction<number>) => {
            state.product.unstoringDaysAfter = action.payload;
        },
        updateProductDeliveryCompany: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.product.deliveryCompany = {
                id: action.payload.id,
                name: action.payload.name,
            };
        },
        updateIsAppOnly: (state, action: PayloadAction<boolean>) => {
            state.product.isAppOnly = action.payload;
        },
        updateProductShippingFee: (state, action: PayloadAction<{ key: string; value: number }>) => {
            state.product.productShippingFee[action.payload.key] = action.payload.value;
        },
        updateIsUndeliverableSecondaryarea: (state, action: PayloadAction<boolean>) => {
            state.product.isUndeliverableSecondaryarea = action.payload;
        },
        updateIsUndeliverableThirdarea: (state, action: PayloadAction<boolean>) => {
            state.product.isUndeliverableThirdarea = action.payload;
        },
        updateFreeShippingFeeConditionPrice: (state, action: PayloadAction<number>) => {
            state.product.freeShippingFeeConditionPrice = action.payload;
        },
        updateBundleShippingFeeType: (state, action: PayloadAction<0 | 1 | 2>) => {
            state.product.bundleShippingFeeType = action.payload;
        },
        updateMaxUnitInBox: (state, action: PayloadAction<number>) => {
            state.product.maxUnitInBox = action.payload;
        },
        updateDistributor: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.product.distributor = {
                ...state.product.distributor,
                id: action.payload.id,
                name: action.payload.name,
            };
        },
        addProductTag: (state, action: PayloadAction<string>) => {
            state.product.tags = [...state.product.tags, { tag: action.payload }];
        },
        removeProductTag: (state, action: PayloadAction<number>) => {
            state.product.tags = state.product.tags.filter((_, i) => i !== action.payload);
        },
        setPropertyJson: (state, action: PayloadAction<{ key: string; value: string }[]>) => {
            state.product.productDetail.propertyJson = action.payload;
        },
        updatePropertyJsonKey: (state, action: PayloadAction<{ index: number; value: string }>) => {
            state.product.productDetail.propertyJson[action.payload.index].key = action.payload.value;
        },
        updatePropertyJsonValue: (state, action: PayloadAction<{ index: number; value: string }>) => {
            state.product.productDetail.propertyJson[action.payload.index].value = action.payload.value;
        },
        removePropertyJson: (state, action: PayloadAction<number>) => {
            state.product.productDetail.propertyJson.splice(action.payload, 1);
        },
        addPropertyJson: state => {
            state.product.productDetail.propertyJson.push({ key: '', value: '' });
        },
        updateShipmentDateOptionType: (state, action: PayloadAction<1 | 2 | 3>) => {
            state.product.shipmentDateOptionType = action.payload;
        },
        updateShipmentDateOptionDay: (state, action: PayloadAction<{ key: string; value: string }>) => {
            const date = state.product.shipmentDateOption ? state.product.shipmentDateOption.split('|') : ['', ''];

            if (action.payload.key === 'shipmentDateOption-day') {
                date[0] = action.payload.value;
            } else {
                date[1] = action.payload.value;
            }

            state.product.shipmentDateOption = date.join('|');
        },
        addProductOptionWithShipmentDate: (state, action: PayloadAction<number>) => {
            // if (!state.product.productDetail.id) {
            // if (state.product.productDetail.productOptionGroups[0].productOptions[0].shipmentDate) {
            //     return;
            // }

            if (
                state.product.productDetail.productOptionGroups.some(pog => {
                    return pog.productOptions.some(po => po.shipmentDate);
                })
            ) {
                return;
            }
            console.log(dateFormatter(new Date(), `MM월 DD일(ddd)${action.payload === 3 ? `부터 순차` : ``} 출고`));
            let defaultOptionWithShipmentDate: any = {
                name: '출고일',
                productOptions: [
                    {
                        name: dateFormatter(
                            new Date(),
                            `MM월 DD일(ddd)${action.payload === 3 ? `부터 순차` : ``} 출고`,
                        ),
                        shipmentDate: dateFormatter(new Date(), 'yyyy-MM-DD'),
                    },
                ],
            };

            if (state.removedProductOptionGroupWithShipmentDate) {
                if (action.payload === 3) {
                    state.removedProductOptionGroupWithShipmentDate.productOptions[0].name =
                        state.removedProductOptionGroupWithShipmentDate.productOptions[0].name.replace(
                            ') 출고',
                            ')부터 순차 출고',
                        );
                } else if (action.payload === 2) {
                    state.removedProductOptionGroupWithShipmentDate.productOptions[0].name =
                        state.removedProductOptionGroupWithShipmentDate.productOptions[0].name.replace(
                            ')부터 순차 출고',
                            ') 출고',
                        );
                }
                defaultOptionWithShipmentDate = { ...state.removedProductOptionGroupWithShipmentDate };
            }

            state.product.productDetail.productOptionGroups = [
                defaultOptionWithShipmentDate,
                ...state.product.productDetail.productOptionGroups,
            ];
            // }
        },
        removeProductOptionWithShipmentDate: state => {
            const index = state.product.productDetail.productOptionGroups.findIndex(optionGroup => {
                return optionGroup.productOptions.some(po => po.shipmentDate);
            });

            if (index >= 0) {
                state.removedProductOptionGroupWithShipmentDate =
                    state.product.productDetail.productOptionGroups[index];

                state.product.productDetail.productOptionGroups.splice(index, 1);
            } else {
                state.removedProductOptionGroupWithShipmentDate = undefined;
            }

            if (state.product.productDetail.productOptionGroups.length === 0) {
                state.product.productDetail.productOptionGroups.push({
                    name: '',
                    productOptions: [
                        {
                            name: '',
                        },
                    ],
                });
            }
        },
        updateShipmentDate: (
            state,
            action: PayloadAction<{ date: string; optionGroupIndex: number; optionIndex: number }>,
        ) => {
            const shipmentDateText =
                state.product.shipmentDateOptionType === 3 ? `MM월 DD일(ddd)부터 순차 출고` : `MM월 DD일(ddd) 출고`;

            state.product.productDetail.productOptionGroups[action.payload.optionGroupIndex].productOptions[
                action.payload.optionIndex
            ].name = dateFormatter(new Date(action.payload.date), shipmentDateText);
            state.product.productDetail.productOptionGroups[action.payload.optionGroupIndex].productOptions[
                action.payload.optionIndex
            ].shipmentDate = dateFormatter(new Date(action.payload.date), 'yyyy-MM-DD');
        },
        removeRemovedProductOptionGroupWithShipmentDate: state => {
            if (state.product.shipmentDateOptionType !== 3) {
                state.removedProductOptionGroupWithShipmentDate = undefined;
            }
        },
        updateMinAppUserGrade: (state, action: PayloadAction<string>) => {
            state.product.minAppUserGrade = +action.payload;
        },
        updateHomeDisplayWithProduct: (state, action: PayloadAction<{ value: boolean; index: number }>) => {
            state.product.homeDisplayWithProduct[action.payload.index].isDisplayed = action.payload.value;
        },
        updateOriginHomeDisplayWithProduct: (state, action: PayloadAction<HomeDisplayWithProduct[]>) => {
            action.payload.forEach(item => {
                const index = state.originHomeDisplayWithProduct.findIndex(
                    temp => temp.displayCategoryId === item.displayCategoryId,
                );
                if (index >= 0) {
                    state.originHomeDisplayWithProduct[index] = item;
                }
            });
        },
        applySpecialPrice: (state, action: PayloadAction<{ specialSaleRate: number; ceilValue: number }>) => {
            state.product.productDetail.productOptionCombinations.forEach(poc => {
                if (state.product.productSaleType.roundingUnit === 0) {
                    poc.productOptionPrice.specialSalePrice = Math.ceil(
                        poc.productOptionPrice.price * ((100 - action.payload.specialSaleRate) / 100),
                    );
                } else {
                    poc.productOptionPrice.specialSalePrice =
                        Math.ceil(
                            (poc.productOptionPrice.price * ((100 - action.payload.specialSaleRate) / 100)) /
                                action.payload.ceilValue,
                        ) * action.payload.ceilValue;
                }
            });
        },
        updateProductSaleTypeSpecialSale: (state, action: PayloadAction<{ key: string; value: any }>) => {
            state.product.productSaleType[action.payload.key] = action.payload.value;
        },
        updateIsRepurchasable: (state, action: PayloadAction<boolean>) => {
            state.product.isRepurchasable = action.payload;
        },
        updateProducMinPurchaseQty: (state, action: PayloadAction<number>) => {
            state.product.minPurchaseQty = action.payload;
        },
        updateProducMaxPurchaseQty: (state, action: PayloadAction<number>) => {
            state.product.maxPurchaseQty = action.payload;
        },
        updateProductRelatedType: (state, action: PayloadAction<ProductRelatedType>) => {
            state.product.productRelated.productRelatedType = action.payload;
        },
        updateProductRelatedDisplayCategory: (state, action: PayloadAction<{ id: number; name: string }>) => {
            state.product.productRelated.displayCategory = {
                id: action.payload.id,
                name: action.payload.name,
            };
        },
        updateProductRelatedTitle: (state, action: PayloadAction<string>) => {
            state.product.productRelated.title = action.payload;
        },
        updateRelatedProductIds: (state, action: PayloadAction<ProductRelatedProduct[]>) => {
            state.product.productRelatedProducts = action.payload;
        },
        updateSummaryTitle: (state, action: PayloadAction<string>) => {
            state.product.summaryTitle = action.payload;
        },
        updateProductTagGroups: (state, action: PayloadAction<{ id: number; tag: string }>) => {
            if (state.product.productTagGroup.some(i => i.id === action.payload.id)) {
                state.product.productTagGroup = state.product.productTagGroup.filter(i => i.id !== action.payload.id);
            } else {
                state.product.productTagGroup.push(action.payload);
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(createSimpleProduct.fulfilled, (state, { payload }) => {
            state.simpleProductId = payload.id;
        });
        builder.addCase(updateProductBasicInfo.fulfilled, (state, { payload }) => {
            state.savedProductType = state.product.productType;
            if (payload?.bundleShippingFeeType !== undefined) {
                state.product.bundleShippingFeeType = payload.bundleShippingFeeType as 0 | 1 | 2;
            }
        });
        builder.addCase(getProductDetail.fulfilled, (state, { payload }) => {
            state.product = payload;
            state.savedProductType = payload.productType;
            state.originHomeDisplayWithProduct = payload.homeDisplayWithProduct;
        });
        builder.addCase(addProductImageRepresent.fulfilled, (state, { payload }) => {
            state.product.productImageRepresent.push(...payload);
        });
        builder.addCase(sortingProductImageRepresent.fulfilled, (state, { payload }) => {
            state.product.productImageRepresent = payload;
        });
        builder.addCase(deleteProductImageRepresent.fulfilled, (state, { payload }) => {
            state.product.productImageRepresent = state.product.productImageRepresent.filter(
                (img, index) => !payload.includes(img.id),
            );
        });
        builder.addCase(addProductImages.fulfilled, (state, { payload }) => {
            state.product.productDetail.productImages.push(...payload);
        });
        builder.addCase(sortingProductImages.fulfilled, (state, { payload }) => {
            state.product.productDetail.productImages = payload;
        });
        builder.addCase(deleteProductImages.fulfilled, (state, { payload }) => {
            state.product.productDetail.productImages = state.product.productDetail.productImages.filter(
                (img, index) => !payload.includes(img.id),
            );
        });
    },
});

export const {
    initProductData,
    setProduct,
    updateProductName,
    updateProductType,
    updateIsInternationalShipping,
    updateProductTargetQuantity,
    updatePrice,
    updateListPrice,
    updateSpecialPrice,
    updateTaxStatus,
    updateAdminMemo,
    updateSummaryTitle,
    updateContents,
    updateRepresentImageFiles,
    updateProductImages,
    updateDeeplink,
    updateVideoUrl,
    updateImageOriginPath,
    updateLabelShippingMethod,
    updateLabelRecommend,
    updateProductOptionCombinations,
    updateProductOptionGroups,
    updateProductOrderConditions,
    updateRestrictedStockOrder,
    updateProductOptionCombinationStatus,
    updateProductOptionCombinationOptionStock,
    updateProductOptionCombinationPriceAndSupplyPrice,
    updateProductOptionGroupName,
    removeProductOption,
    addProductOption,
    updateProductOptionName,
    updateCategory,
    updateChildCategory,
    updateProductStatus,
    updateProductSaleType,
    updateLimitedPeriodSaleEndDate,
    updateIsCommingSoon,
    updateCommingSoonDate,
    updateNoResale,
    updateIsVisibleInList,
    updateUseGift,
    updateGiftExpireType,
    updateGiftExpireDay,
    updateGiftFixedExpireDate,
    updateUnstoringDaysAfter,
    updateProductDeliveryCompany,
    updateProductShippingFee,
    updateFreeShippingFeeConditionPrice,
    updateBundleShippingFeeType,
    updateMaxUnitInBox,
    updateDistributor,
    addProductTag,
    removeProductTag,
    setPropertyJson,
    updatePropertyJsonKey,
    updatePropertyJsonValue,
    removePropertyJson,
    addPropertyJson,
    updateShipmentDateOptionType,
    updateShipmentDateOptionDay,
    addProductOptionWithShipmentDate,
    removeProductOptionWithShipmentDate,
    updateShipmentDate,
    removeRemovedProductOptionGroupWithShipmentDate,
    updateMinAppUserGrade,
    updateHomeDisplayWithProduct,
    updateOriginHomeDisplayWithProduct,
    applySpecialPrice,
    updateProductSaleTypeSpecialSale,
    updateUseKakaosync,
    updateProductRelatedType,
    updateProductRelatedDisplayCategory,
    updateProductRelatedTitle,
    updateRelatedProductIds,
    updateIsUndeliverableSecondaryarea,
    updateIsUndeliverableThirdarea,
    updateIsAppOnly,
    updateProductTagGroups,
    initSelectedComb,
    updateProducMinPurchaseQty,
    updateProducMaxPurchaseQty,
    updateIsRepurchasable,
    updateTossVariantKey,
} = productSlice.actions;
export default productSlice.reducer;
